import { supabase } from '../lib/supabaseClient';

const REPORT_IMAGE_BUCKET =
  process.env.REACT_APP_SUPABASE_HOSPITAL_REPORT_BUCKET || 'hospital-report-images';

function decodeBase64ToBlob(dataUrl) {
  const match = dataUrl.match(/^data:(image\/([a-zA-Z0-9.+-]+));base64,(.+)$/);
  if (!match) {
    return null;
  }

  const mimeType = match[1];
  const extension = match[2] === 'jpeg' ? 'jpg' : match[2];
  const base64Body = match[3];
  const binary = window.atob(base64Body);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return {
    blob: new Blob([bytes], { type: mimeType }),
    extension,
    cacheKey: base64Body,
  };
}

async function uploadDataUrlImage(dataUrl, context, uploadedMap) {
  const decoded = decodeBase64ToBlob(dataUrl);
  if (!decoded) {
    return dataUrl;
  }

  const cached = uploadedMap.get(decoded.cacheKey);
  if (cached) {
    return cached;
  }

  if (!supabase) {
    throw new Error('Supabase Storage が設定されていません。');
  }

  const cohortSegment = context.cohortId || 'unknown-cohort';
  const capacitySegment = context.capacityId || 'unknown-capacity';
  const fileName = `${Date.now()}-${crypto.randomUUID()}.${decoded.extension}`;
  const filePath = `hospital-reports/${cohortSegment}/${capacitySegment}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from(REPORT_IMAGE_BUCKET)
    .upload(filePath, decoded.blob, {
      cacheControl: '3600',
      contentType: decoded.blob.type,
      upsert: false,
    });

  if (uploadError) {
    throw new Error(`画像アップロードに失敗しました: ${uploadError.message}`);
  }

  const { data } = supabase.storage.from(REPORT_IMAGE_BUCKET).getPublicUrl(filePath);
  const publicUrl = data?.publicUrl;
  if (!publicUrl) {
    throw new Error('画像 URL の取得に失敗しました。');
  }

  uploadedMap.set(decoded.cacheKey, publicUrl);
  return publicUrl;
}

async function replaceEmbeddedImagesInHtml(html, context, uploadedMap) {
  if (!html || !html.includes('data:image/')) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = Array.from(doc.querySelectorAll('img'));

  for (const image of images) {
    const src = image.getAttribute('src') || '';
    if (!src.startsWith('data:image/')) {
      continue;
    }

    const uploadedUrl = await uploadDataUrlImage(src, context, uploadedMap);
    image.setAttribute('src', uploadedUrl);
  }

  return doc.body.innerHTML;
}

export async function prepareHospitalReportPayloadImages(payload, context) {
  const uploadedMap = new Map();
  const htmlFields = [
    'schedule',
    'training',
    'assignment',
    'facility',
    'other',
    'hospital_tour_text',
  ];

  const nextPayload = { ...payload };
  for (const field of htmlFields) {
    nextPayload[field] = await replaceEmbeddedImagesInHtml(
      nextPayload[field],
      context,
      uploadedMap,
    );
  }

  if (Array.isArray(nextPayload.doctors)) {
    nextPayload.doctors = await Promise.all(
      nextPayload.doctors.map(async (doctor) => ({
        ...doctor,
        doctor_note: await replaceEmbeddedImagesInHtml(
          doctor.doctor_note,
          context,
          uploadedMap,
        ),
      })),
    );
  }

  return nextPayload;
}
